import React, { Component } from 'react';

import { arrayMoveImmutable as arrayMove } from 'array-move';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import { Button, Pane, PlusIcon } from 'evergreen-ui';

import configuration from '../../../util/Configuration';
import Api from '../../../util/Api';
import KDropzoneDialog from './KDropzoneDialog';
import KImageComponent from './KImageComponent';
import DesignEditorModal from '../DesignEditor/DesignEditorModal';

// Saved product images live in the product-images keyspace (configuration.images
// = REACT_APP_IMAGE_STORAGE), same as ProductInfoBlock. NOT the catalog bucket —
// catalog-images holds catalog-import files keyed by imageUrl, so product UUIDs
// 404 there (that swap broke edit-form previews once already).
const SortableItem = SortableElement((props) => {
    const currentThumbnail =  props.value.id ?
                (`${configuration.images}/thumb_${props.value.image}.jpg`) :
                    props.value.data
    const currentImage = props.value.id ?
                (`${configuration.images}/original_${props.value.image}.jpg`) :
                    props.value.data

    return (
        <Pane display="inline-block" cursor="pointer">
            <KImageComponent
                onRemove={props.onRemove}
                onEdit={() => props.onEdit(currentImage)}
                image={currentThumbnail}
                fullImage={currentImage}/>
        </Pane>)
})

const SortableList = SortableContainer((props) => {
    const items = props.items;

    return (
        <Pane display="flex" flexWrap="wrap">
            {items.map((value, index) => (
                <SortableItem onRemove={() => { props.onRemove(index) }}
                    onEdit={(value) => { props.onEdit(value, index) }}
                    key={`product-image-${index}`}
                    index={index}
                    value={value}/>
            ))}
        </Pane>
    )
})

export default class KFileDrop extends Component {
    constructor (props) {
        super(props);

        this.state = {
            open: false,
            editing: false,
            editingIndex: null,
            editingImage: null, // data URL or image URL
            editingDesignJson: null, // loaded fabric.js JSON
        };
    }

    readFileAsync = (file) => {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();

            reader.onload = () => {
                resolve(reader.result);
            };

            reader.onerror = reject;

            reader.readAsDataURL(file);
        })
    }

    handleClose = () => {
        this.setState({
            open: false
        })
    }

    handleOpen = () => {
        this.setState({
            open: true
        })
    }

    handleRemove = (index) => {
        this.updateFiles(this.props.form.values.images.filter((v, i) => i !== index));
    }

    handleEditSave = (dataUrl, designJson) => {
        const index = this.state.editingIndex;
        const updatedFiles = [...this.props.form.values.images];
        const existing = updatedFiles[index];

        updatedFiles[index] = { data: dataUrl, position: index, _pendingDesignJson: designJson };

        // If the image was already saved (has a UUID filename), save design JSON immediately
        if (existing && existing.image) {
            Api.put(`/image-designs/${encodeURIComponent(existing.image)}`, {}, {}, {
                designJson,
            }).catch(err => console.error('Failed to save design JSON:', err));
        }

        this.updateFiles(updatedFiles);
        this.setState({
            editing: false,
            editingIndex: null,
            editingImage: null,
            editingDesignJson: null,
        });
    }

    handleEdit = async (uri, index) => {
        const images = this.props.form.values[this.props.field.name] || [];
        const imageEntry = images[index];

        // Check for pending (unsaved) design JSON first
        let designJson = null;
        if (imageEntry && imageEntry._pendingDesignJson) {
            designJson = imageEntry._pendingDesignJson;
        } else if (imageEntry && imageEntry.image) {
            // Try to load from API for saved images
            try {
                const response = await Api.get(`/image-designs/${encodeURIComponent(imageEntry.image)}`);
                if (response.data && response.data.designJson) {
                    designJson = response.data.designJson;
                }
            } catch (e) {
                // 404 = no saved design, that's fine
            }
        }

        // Already-saved images are remote CDN URLs; the canvas editor needs
        // pixel access (crossOrigin), which the CDN doesn't grant, so it can't
        // load/export them. Route them through the API proxy to get a clean,
        // same-policy base64 data URL. Newly-added images are already base64.
        const editingImage = await this.toEditableImage(uri);

        this.setState({
            editing: true,
            editingIndex: index,
            editingImage,
            editingDesignJson: designJson,
        });
    }

    toEditableImage = async (uri) => {
        if (!uri || typeof uri !== 'string' || uri.startsWith('data:')) {
            return uri;
        }

        try {
            const response = await Api.get('/image-proxy', {}, { url: uri });
            if (response && response.data) {
                return response.data;
            }
        } catch (e) {
            console.error('Failed to proxy image for editing:', e);
        }

        // Fall back to the raw URL — the editor will still try, and will show a
        // toast if the CDN blocks pixel access.
        return uri;
    }

    handleSave = async (files) => {
        let processedFiles = await Promise.all(files.map(async file => {
            const data = await this.readFileAsync(file);

            return {
                data
            }
        }))

        processedFiles = ((this.props.form.values[this.props.field.name] || []).concat(processedFiles))
        .map((image, index) => ({
            ...image,
            position: index
        }));

        this.updateFiles(processedFiles);

        this.setState({
            open: false
        })
    }

    updateFiles= (files) => {
        this.props.form.setFieldValue(this.props.field.name, files);
    }

    handleSortEnd = ({oldIndex, newIndex}) => {
        let reordered = arrayMove((this.props.form.values[this.props.field.name] || []), oldIndex, newIndex);
        reordered = reordered.map((image, index) => {
            return {
                ...image,
                position: index
            }
        });

        this.updateFiles(reordered);
    }

    render() {
        return (
            <Pane marginBottom={16}>
                <Button type="button" appearance="primary" intent="success" onClick={this.handleOpen.bind(this)}>
                    <PlusIcon/>
                </Button>
                <Pane>
                    <SortableList axis="xy" pressDelay={200} items={this.props.form.values[this.props.field.name] || []}
                        onSortEnd={this.handleSortEnd}
                        onRemove={this.handleRemove}
                        onEdit={this.handleEdit}
                        />
                </Pane>

                <KDropzoneDialog
                    onConfirm={this.handleSave}
                    onClose={this.handleClose}
                    isOpen={this.state.open}/>


                 <DesignEditorModal
                    open={this.state.editing}
                    title="Editează imaginea produsului"
                    sizeToImage
                    initialImage={this.state.editingImage}
                    designJson={this.state.editingDesignJson}
                    onClose={() => this.setState({ editing: false, editingIndex: null, editingImage: null, editingDesignJson: null })}
                    onExport={this.handleEditSave}
                 />
            </Pane>
        )
    }
}
